import type { Timestamp } from '@bufbuild/protobuf/wkt';
import { timestampDate } from '@bufbuild/protobuf/wkt';
import type { PreviewReleaseDiffResponse } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import type { Release } from '@sunstone/api-client/gen/releases/v1/releases_pb';
import type { WorkflowRunLogEvent } from '@sunstone/api-client/hooks';
import { ReleaseDiffStatus } from '@sunstone/api-client/hooks';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@sunstone/ui';
import { AlertCircle, CheckCircle2, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { ReleaseDiffTable } from '@/components/ui/ReleaseDiffTable';
import { ReleaseSyncStatusAlert } from '@/components/ui/ReleaseSyncStatusAlert';
import { relativeTime } from '@/lib/time';

interface ReleaseDiffDialogSyncProps {
  /** Called when the user clicks "Sync now" on a SYNC_NEEDED response. */
  onSyncRelease?: () => void;
  /** True while a just-triggered sync is still in progress (covers both the
   * mutation's own in-flight window and the polling wait afterward). */
  isSyncingRelease?: boolean;
  syncReleaseError?: Error | null;
}

interface ReleaseDiffDialogConfirmProps {
  /** Shows a confirm step (checkbox + Promote button) once the diff is
   * ready, calling PromoteRelease to actually apply it to the workspace's
   * cluster. Omit the whole `confirm` prop to keep the dialog read-only.
   * Called with the current "Run Liquibase migrations" checkbox value. */
  onConfirmUpgrade: (runLiquibase: boolean) => void;
  isConfirming?: boolean;
  confirmError?: Error | null;
  /** True when this preview targets a different release than the one
   * currently running - affects the confirm button label only; a
   * same-release refresh promote is otherwise identical. */
  isUpgrade?: boolean;
}

interface ReleaseDiffDialogPlanProps {
  /** When provided, shows a "Plan" button next to Promote that dispatches a
   * Terraform dry-run against the same target release. Never gates or
   * disables Promote - a user can promote with or without ever running a
   * plan, and a failed plan doesn't block it. Called with the current "Run
   * Liquibase migrations" checkbox value. */
  onStartPlan?: (runLiquibase: boolean) => void;
  /** True while a plan dispatch/run is in flight (disables the Plan button
   * itself only - see above). */
  isPlanning?: boolean;
  /** Events delivered so far for the current plan run, if any - fed to
   * LiveLogPanel once the status alert below is expanded. */
  events?: WorkflowRunLogEvent[];
  /** Dispatch/stream-transport error - distinct from the run's own outcome,
   * which arrives via status/conclusion. */
  error?: Error | null;
  /** 'in_progress' | 'completed' | undefined (nothing to show, matches
   * today's behavior with no plan ever run or resumed). Drives
   * PlanStatusAlert - see its own doc comment for the full state table. */
  status?: string;
  /** 'success' | 'failure' | 'cancelled' | 'skipped' | undefined; only
   * meaningful once status === 'completed'. */
  conclusion?: string;
  /** When the resumed run finished - unset for a run that just completed
   * live in this session (its own "completed" event carries no wall-clock
   * time), in which case the alert says "completed" without a relative time. */
  completedAt?: Timestamp;
  /** Direct URL to the GitHub Actions run page for the found/current plan
   * run (see GetLatestReleasePlanRunResponse.run_url). Unset while a fresh
   * dispatch hasn't yet been registered by x-forge-installer's callback. */
  runUrl?: string;
  /** True once the frontend has given up waiting on the stream for a
   * terminal event - the backend run may have finished without us hearing
   * about it. */
  timedOut?: boolean;
}

interface ReleaseDiffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  data?: PreviewReleaseDiffResponse;
  /** The target release, kept continuously fresh via the caller's live
   * subscription (e.g. useStreamReleaseEvents' cache invalidation) rather
   * than `data.targetRelease`, which only refreshes when the diff itself is
   * re-computed. ReleaseSyncStatusAlert prefers this for syncStatus/syncError/
   * lastSyncedAt/isStale so a sync's outcome is reflected the instant it
   * lands, not on the next diff re-fetch. Falls back to `data.targetRelease`
   * while this hasn't loaded yet (e.g. right as the dialog opens). */
  liveTargetRelease?: Release;
  /** RPC-transport-level error (network, permission, ...) — distinct from a
   * domain-level sync failure, which arrives as data.status === FAILED. */
  requestError?: Error | null;
  /** True when this preview targets a different release than the one
   * currently running - drives the dialog's own title ("Upgrade Workspace"
   * vs "Promote Workspace"). Kept separate from confirm.isUpgrade (which
   * only affects the confirm button's label) so the title is correct even
   * when confirm is omitted entirely (the caller lacks
   * promote:workspace/upgrade:workspace). */
  isUpgrade?: boolean;
  sync?: ReleaseDiffDialogSyncProps;
  confirm?: ReleaseDiffDialogConfirmProps;
  plan?: ReleaseDiffDialogPlanProps;
}

/** Shown alongside Plan/Promote when the target release's own sync is
 * currently in progress - its last-known-good manifest is fine to preview
 * (see the diff rendering below), but not to actually apply or plan
 * against mid-resync. Exported so callers driving the same disabled state
 * elsewhere (if any) use identical wording. */
export const RELEASE_SYNC_IN_PROGRESS_REASON =
  'A release sync is in progress — try again once it completes.';

function ConfirmUpgradeSection({
  isConfirming,
  confirmError,
  onConfirm,
  targetVersion,
  isUpgrade,
  onStartPlan,
  isPlanning,
  planError,
  blockedReason,
}: {
  isConfirming: boolean;
  confirmError?: Error | null;
  onConfirm: (runLiquibase: boolean) => void;
  targetVersion?: string;
  isUpgrade: boolean;
  onStartPlan?: (runLiquibase: boolean) => void;
  isPlanning?: boolean;
  planError?: Error | null;
  /** Set while the target release's sync is in progress - disables both
   * Plan and Promote/Apply with an explanatory tooltip, independent of
   * isConfirming/isPlanning's own pending-state disabling. */
  blockedReason?: string;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [runLiquibase, setRunLiquibase] = useState(true);
  const label = isUpgrade ? (targetVersion ? `Promote to ${targetVersion}` : 'Promote') : 'Apply';
  const pendingLabel = isUpgrade ? 'Promoting…' : 'Applying…';

  return (
    <div className="space-y-3 border-t pt-4">
      <div className="flex items-start gap-2.5">
        <Checkbox
          id="run-liquibase"
          checked={runLiquibase}
          onCheckedChange={(v) => setRunLiquibase(v === true)}
          className="mt-0.5"
        />
        <Label
          htmlFor="run-liquibase"
          className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
        >
          Run Liquibase migrations
        </Label>
      </div>
      {confirmError && (
        <p className="text-sm text-destructive">
          {confirmError.message ?? 'Failed to start the release promote.'}
        </p>
      )}
      {planError && (
        <p className="text-sm text-destructive">
          {planError.message ?? 'Failed to start the release plan.'}
        </p>
      )}
      <DialogFooter>
        {onStartPlan && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    disabled={isPlanning || !!blockedReason}
                    onClick={() => onStartPlan(runLiquibase)}
                  >
                    {isPlanning ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Planning…
                      </>
                    ) : (
                      'Plan'
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              {blockedReason && <TooltipContent>{blockedReason}</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
        )}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  className="whitespace-normal text-center"
                  disabled={isConfirming || !!blockedReason}
                  onClick={() => setConfirmOpen(true)}
                >
                  {isConfirming ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {pendingLabel}
                    </>
                  ) : (
                    label
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            {blockedReason && <TooltipContent>{blockedReason}</TooltipContent>}
          </Tooltip>
        </TooltipProvider>
      </DialogFooter>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          setConfirmOpen(open);
          if (!open) setUnderstood(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{label}</AlertDialogTitle>
            <AlertDialogDescription>
              This will restart services in this workspace and may cause a brief interruption.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2.5">
            <Checkbox
              id="confirm-upgrade"
              checked={understood}
              onCheckedChange={(v) => setUnderstood(v === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="confirm-upgrade"
              className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
            >
              I understand and want to continue.
            </Label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!understood}
              onClick={() => {
                setConfirmOpen(false);
                setUnderstood(false);
                onConfirm(runLiquibase);
              }}
            >
              {label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Persistent plan-status banner shown alongside the Plan/Apply section, once
 * the sync diff is available (see showPlanGroup) - always renders one of
 * four states (not started / running / success / error) rather than
 * disappearing when no plan has ever run, so it stays a stable anchor next
 * to the always-visible Liquibase checkbox and Plan/Promote buttons.
 *
 * A terminal result (success/failure) also gets flagged stale when
 * `syncedAt` is newer than `completedAt` - the plan's own ~1hr backend
 * freshness window is independent of the release's, so a resync can land
 * well inside that window and leave a now-outdated plan outcome on screen
 * with nothing else to signal it no longer reflects the current diff.
 */
function PlanStatusAlert({
  status,
  conclusion,
  completedAt,
  isTimedOut,
  runUrl,
  syncedAt,
}: {
  status?: string;
  conclusion?: string;
  completedAt?: Timestamp;
  isTimedOut: boolean;
  runUrl?: string;
  syncedAt?: Timestamp;
}) {
  const isRunning = status === 'in_progress' && !isTimedOut;
  const isFailed =
    conclusion === 'failure' || conclusion === 'cancelled' || conclusion === 'skipped';
  const isSucceeded = status === 'completed' && conclusion === 'success';
  const isStale =
    (isSucceeded || isFailed) &&
    !!completedAt &&
    !!syncedAt &&
    timestampDate(syncedAt).getTime() > timestampDate(completedAt).getTime();

  let title: string;
  let message: string;
  if (isTimedOut) {
    title = 'Plan status unknown';
    message = 'Lost track of this plan — it may still be running. Check back later.';
  } else if (isRunning) {
    title = 'Plan running';
    message = 'Terraform dry-run in progress…';
  } else if (isFailed) {
    title = 'Plan failed';
    message = completedAt ? `Failed ${relativeTime(timestampDate(completedAt))}.` : 'Failed.';
  } else if (isSucceeded) {
    title = 'Plan completed';
    message = completedAt ? `Completed ${relativeTime(timestampDate(completedAt))}.` : 'Completed.';
  } else {
    title = 'Plan not started';
    message = 'Run Plan to preview the infrastructure changes before promoting.';
  }
  if (isStale) {
    message +=
      ' This workspace has been re-synced since — run Plan again to preview the latest changes.';
  }

  return (
    <Alert variant={isFailed ? 'destructive' : 'default'}>
      {isFailed ? (
        <AlertCircle className="h-4 w-4" />
      ) : isSucceeded ? (
        <CheckCircle2 className="h-4 w-4" />
      ) : (
        <RefreshCw className={isRunning ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
      )}
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {message}
        {runUrl && (
          <>
            {' '}
            <a
              href={runUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5"
            >
              <ExternalLink className="w-3 h-3" /> View in GitHub
            </a>
          </>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * Shows the result of a PreviewReleaseDiff call: a loading state while the
 * sync runs (this can take minutes), then the release's own metadata plus a
 * single git-diff-style table of every added/changed/removed microservice
 * (sorted alphabetically, tagged A/M/D) and the shared xforge-db platform
 * component, or the failure reason. When onConfirmUpgrade is provided, a
 * confirm step appears once the diff is ready so the whole
 * preview-then-promote flow lives in one dialog behind a single trigger
 * button - PromoteRelease covers both a same-release refresh (isUpgrade
 * false) and a cross-major upgrade (isUpgrade true) identically.
 *
 * The diff table renders whenever status === READY, independent of whether
 * the target release's own sync is currently in progress or previously
 * failed - a release that has ever synced successfully has a usable
 * manifest no matter how old or how its *current* sync attempt is faring
 * (see docs/design/005-release-sync-and-promote.md). Only Plan/Promote
 * themselves are disabled while a resync is in progress (isSyncingRelease),
 * via ConfirmUpgradeSection's blockedReason - the diff stays visible and
 * interactive throughout.
 */
export function ReleaseDiffDialog({
  open,
  onOpenChange,
  isPending,
  data,
  liveTargetRelease,
  requestError,
  isUpgrade = false,
  sync,
  confirm,
  plan,
}: ReleaseDiffDialogProps) {
  const { onSyncRelease, isSyncingRelease = false, syncReleaseError } = sync ?? {};
  const {
    onConfirmUpgrade,
    isConfirming = false,
    confirmError,
    isUpgrade: confirmIsUpgrade = false,
  } = confirm ?? {};
  const {
    onStartPlan,
    isPlanning = false,
    error: planError,
    status: planStatus,
    conclusion: planConclusion,
    completedAt: planCompletedAt,
    runUrl: planRunUrl,
    timedOut: planTimedOut = false,
  } = plan ?? {};

  const diff = data?.diff;
  const needsSync = data?.status === ReleaseDiffStatus.SYNC_NEEDED;
  const targetVersion = diff?.release?.version;
  // Sourced from liveTargetRelease first - a plain read that resolves as
  // soon as the dialog opens, independent of PreviewReleaseDiff/sync - so
  // the title shows a version immediately rather than only once the diff
  // itself has loaded.
  const titleVersion = liveTargetRelease?.version || targetVersion;
  // Same precedence ReleaseSyncStatusAlert uses internally - liveTargetRelease
  // wins when loaded since it's kept fresh by the live subscription,
  // independent of the diff's own re-fetch cadence. Used to flag a stale
  // plan result below (see PlanStatusAlert's doc comment).
  const lastSyncedAt = liveTargetRelease?.lastSyncedAt ?? data?.lastSyncedAt;

  const showSyncGroup = isPending || !!requestError || !!data;
  // A diff is "ready" purely from the RPC's own status - deliberately NOT
  // gated on isSyncingRelease, so a diff computed from a last-known-good
  // manifest stays visible (and Plan/Promote enabled or not, per
  // blockedReason below) even while a resync of that same release is
  // running in the background.
  const isReady = !isPending && !requestError && data?.status === ReleaseDiffStatus.READY && !!diff;
  const showConfirmSection = isReady && !!onConfirmUpgrade;
  // The Plan & Apply block (including its "not started" alert) only appears
  // once the sync diff is actually available, not while
  // loading/erroring/SYNC_NEEDED - independent of showConfirmSection, since a
  // read-only preview (no onConfirmUpgrade) can still surface a plan's status.
  const showPlanGroup = isReady;
  // Plan/Promote (not the diff itself) are disabled while the target
  // release's own sync is in progress - applying or planning against a
  // manifest that may be overwritten mid-request isn't safe, even though
  // previewing it is (see resolvePromotionTarget's doc comment, backend).
  const blockedReason = isSyncingRelease ? RELEASE_SYNC_IN_PROGRESS_REASON : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {isUpgrade
              ? titleVersion
                ? `Upgrade Workspace - ${titleVersion}`
                : 'Upgrade Workspace'
              : titleVersion
                ? `Promote Workspace - ${titleVersion}`
                : 'Promote Workspace'}
          </DialogTitle>
          <DialogDescription>
            {isPending
              ? 'Loading the release diff…'
              : requestError
                ? 'Failed to load the release diff.'
                : needsSync
                  ? 'This release needs a fresh sync before it can be previewed.'
                  : isReady && isSyncingRelease
                    ? 'Showing the diff from the last successful sync while a new sync runs in the background.'
                    : isSyncingRelease
                      ? 'Waiting for the release sync to complete — this can take several minutes.'
                      : onConfirmUpgrade
                        ? 'Review the release before promoting it to this workspace.'
                        : 'Changes found since this workspace last synced.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
          {showSyncGroup && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Release Sync</h4>

              {!isPending && data && (
                <ReleaseSyncStatusAlert
                  data={data}
                  liveTargetRelease={liveTargetRelease}
                  onSyncRelease={onSyncRelease}
                  isSyncingRelease={isSyncingRelease}
                  syncReleaseError={syncReleaseError}
                />
              )}

              {isPending && (
                <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <p className="text-sm">Loading…</p>
                </div>
              )}

              {!isPending && requestError && (
                <div className="flex items-start gap-2 py-6 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <p>{requestError.message ?? 'Failed to start the release sync.'}</p>
                </div>
              )}

              {!isPending && !requestError && data?.status === ReleaseDiffStatus.FAILED && (
                <div className="flex items-start gap-2 py-6 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <p>{data.errorMessage || 'The release sync failed.'}</p>
                </div>
              )}

              {isReady && <ReleaseDiffTable diff={diff} />}
            </div>
          )}
        </div>

        {showPlanGroup && (
          <div className="space-y-3 border-t pt-4">
            <h4 className="text-sm font-semibold text-foreground">
              Release Promote - Plan & Apply
            </h4>

            <PlanStatusAlert
              status={planStatus}
              conclusion={planConclusion}
              completedAt={planCompletedAt}
              isTimedOut={planTimedOut}
              runUrl={planRunUrl}
              syncedAt={lastSyncedAt}
            />
          </div>
        )}

        {showConfirmSection && onConfirmUpgrade && (
          <ConfirmUpgradeSection
            isConfirming={isConfirming}
            confirmError={confirmError}
            onConfirm={onConfirmUpgrade}
            targetVersion={targetVersion}
            isUpgrade={confirmIsUpgrade}
            onStartPlan={onStartPlan}
            isPlanning={isPlanning}
            planError={planError}
            blockedReason={blockedReason}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
